import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { hreflangLinks } from "@/lib/seo";

const URL_FI = "https://montessori24.fi/historia";
const TITLE = "Montessorin ja Piklerin historia | Montessori24";
const DESC =
  "Maria Montessorin ja Emmi Piklerin tarina, pedagogiikan historia Suomessa ja aikajana keskeisistä virstanpylväistä — selkeästi suomeksi.";

export const Route = createFileRoute("/historia")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "montessori historia, pikler menetelmä, maria montessori, emmi pikler, oppimistorni historia, montessori suomi" },
      { property: "og:type", content: "article" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL_FI },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:site_name", content: "Montessori24" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL_FI }, ...hreflangLinks(URL_FI)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: TITLE,
          description: DESC,
          url: URL_FI,
          inLanguage: "fi-FI",
          publisher: { "@type": "Organization", name: "Montessori24", url: "https://montessori24.fi" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
            { "@type": "ListItem", position: 2, name: "Historia", item: URL_FI },
          ],
        }),
      },
    ],
  }),
  component: HistoriaPage,
});

type Milestone = { year: string; title: string; body: string };

const TIMELINE: ReadonlyArray<Milestone> = [
  { year: "1870", title: "Maria Montessori syntyy", body: "Chiaravalle, Italia. Tulevaisuuden lääkäri, tutkija ja pedagogi." },
  { year: "1896", title: "Italian ensimmäisiä naislääkäreitä", body: "Maria valmistuu Rooman yliopistosta lääkäriksi." },
  { year: "1902", title: "Emmi Pikler syntyy", body: "Wien, Itävalta. Tulevaisuuden pediatri ja vapaan liikkeen puolestapuhuja." },
  { year: "1907", title: "Casa dei Bambini", body: "Rooman San Lorenzon köyhäinkortteliin avataan ensimmäinen 'lasten talo'. Menetelmä syntyy." },
  { year: "1909", title: "Il Metodo", body: "Montessorin ensimmäinen kirja julkaistaan; se käännetään pian yli 20 kielelle." },
  { year: "1929", title: "AMI perustetaan", body: "Association Montessori Internationale Amsterdamissa vaalimaan menetelmän aitoutta." },
  { year: "1946", title: "Lóczy avataan", body: "Emmi Pikler johtaa Budapestin orpokodin, jossa kehitetään vapaan liikkeen ja kunnioittavan hoivan malli." },
  { year: "1950-luku", title: "Montessori Suomeen", body: "Ensimmäiset Montessori-luokat ja -päiväkodit alkavat toimia Helsingissä ja Tampereella." },
  { year: "1969", title: "Peaceful Babies", body: "Piklerin pääteos vapaasta liikkeestä julkaistaan. Tulokset perustuvat 15 vuoden videotutkimukseen." },
  { year: "1984", title: "Emmi Pikler kuolee", body: "Työtä jatkaa tytär Anna Tardos ja Pikler-instituutti Budapestissa." },
  { year: "2010-luku", title: "Kotimontessori nousee", body: "Suomalaiset vanhemmat löytävät oppimistornin, Pikler-kolmion ja lattiapatjan osaksi arkea." },
  { year: "2020-luku", title: "Montessori24 syntyy", body: "Suomalainen perhe alkaa tuoda koivuvanerikalusteita koteihin Pohjoismaissa." },
];

function HistoriaPage() {
  return (
    <Layout>
      {/* HERO */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-20 md:py-28">
          <div className="grid gap-10 md:grid-cols-[1.3fr_1fr] md:gap-16 md:items-end">
            <div>
              <span className="text-xs uppercase tracking-[0.28em] text-clay">Historia · 1870 → tänään</span>
              <h1 className="mt-6 font-display text-5xl leading-[1.05] text-foreground md:text-7xl text-balance">
                Kasvun juuret — Montessorin ja Piklerin tarina
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                Miten yli 100 vuotta vanha filosofia — kaksi rohkeaa lääkäriä, kaksi rakastavaa havaintoa lapsesta — tukee tänään suomalaisen perheen arkea.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                <span>Maria Montessori</span>
                <span className="h-px w-8 bg-border" />
                <span>Emmi Pikler</span>
                <span className="h-px w-8 bg-border" />
                <span>Suomessa 1950 →</span>
              </div>
            </div>
            <aside className="border-l-2 border-clay pl-6">
              <blockquote className="font-display text-2xl leading-snug text-foreground md:text-3xl">
                "The child is both a hope and a promise for mankind."
              </blockquote>
              <figcaption className="mt-4 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                Maria Montessori · Education for a New World (1946)
              </figcaption>
            </aside>
          </div>
        </div>
      </section>

      {/* MARIA MONTESSORI */}
      <section className="border-b border-border bg-background">
        <div className="container-prose py-16 md:py-24">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <span className="text-xs uppercase tracking-[0.25em] text-clay">Osa 1</span>
              <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
                Maria Montessori
              </h2>
              <p className="mt-2 text-sm uppercase tracking-[0.18em] text-muted-foreground">1870 – 1952</p>
              <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                Lääkäri, antropologi ja Nobelin rauhanpalkintoehdokas kolmesti. Hänen menetelmäänsä sovelletaan tänään yli 20 000 koulussa yli 100 maassa.
              </p>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="text-lg leading-relaxed text-foreground">
                Maria Montessori valmistui Rooman yliopistosta lääkäriksi vuonna 1896 — yhtenä Italian ensimmäisistä naislääkäreistä. Työskennellessään psykiatrisella osastolla hän kohtasi lapsia, joita pidettiin "opetuskelvottomina", ja huomasi että kyse ei ollut heidän puutteistaan vaan ympäristön puutteista.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Vuonna 1907 hän avasi Rooman San Lorenzon köyhäinkortteliin <em>Casa dei Bambini</em> — lasten talon. Kalusteet mitoitettiin lapsen kokoisiksi, materiaalit asetettiin matalille avoimille hyllyille ja lapset saivat itse valita työnsä. Tulokset yllättivät kaikki: 3–6-vuotiaat oppivat lukemaan ja kirjoittamaan itse, keskittyivät tunteja ja rauhoittuivat, kun ympäristö kunnioitti heitä.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Havainnoista syntyivät ne periaatteet, jotka kantavat menetelmää yhä:
              </p>
              <ul className="mt-4 space-y-2 text-muted-foreground">
                <li><strong className="text-foreground">Valmisteltu ympäristö</strong> — kaikki on lapsen mittakaavassa ja saavutettavissa.</li>
                <li><strong className="text-foreground">Herkkyyskaudet</strong> — tietyt taidot opitaan tiettyinä ikävaiheina helpoimmin.</li>
                <li><strong className="text-foreground">Absorboiva mieli</strong> — 0–6-vuotias imee kielen ja kulttuurin tiedostamattaan.</li>
                <li><strong className="text-foreground">Vapaus rajojen sisällä</strong> — lapsi valitsee itse, mutta valittavat vaihtoehdot ovat kaikki hyviä.</li>
                <li><strong className="text-foreground">Normalisaatio</strong> — keskittynyt työ tuo sisäisen rauhan ja itsekurin.</li>
              </ul>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Montessori kirjoitti yli 20 kirjaa, koulutti opettajia kuudella mantereella ja vietti sotavuodet Intiassa Rabindranath Tagoren kutsumana. Hän kuoli 6. toukokuuta 1952 Noordwijkissa Alankomaissa.
              </p>
              <Link to="/montessori-koulu/maria-montessori-elamakerta" className="mt-6 inline-flex text-sm font-medium text-foreground underline underline-offset-4 hover:text-clay">
                Lue laajempi elämäkerta →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* EMMI PIKLER */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-16 md:py-24">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <span className="text-xs uppercase tracking-[0.25em] text-clay">Osa 2</span>
              <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
                Emmi Pikler
              </h2>
              <p className="mt-2 text-sm uppercase tracking-[0.18em] text-muted-foreground">1902 – 1984</p>
              <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                Unkarilainen pediatri, joka osoitti tieteellisesti, että vauva oppii liikkumaan itse — ilman aikuisen ohjausta. Pikler-kolmio ja lattiapatja ovat hänen työnsä perintöä.
              </p>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="text-lg leading-relaxed text-foreground">
                Wienissä syntynyt Emmi Pikler opiskeli lääketiedettä ja työskenteli perhelääkärinä Budapestissa. Hän seurasi kymmeniä perheitä pitkiä aikoja ja huomasi, että lapset, joita ei istutettu, kävelytetty tai autettu asentoihin, liikkuivat aikuisena varmemmin ja turvallisemmin.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Vuonna 1946 Unkarin valtio pyysi Pikleria johtamaan uutta orpokotia Budapestin Lóczy-kadulla. <strong>Lóczy</strong> toimi 65 vuotta ja kasvatti yli 3 000 lasta. Sen perusperiaatteet — pysyvät hoitajat, ennakoitavat rutiinit, kunnioittava kieli ja vapaa liike — ovat tänään pohjana neuvoloiden ja päiväkotien vauvatyössä ympäri maailmaa.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Piklerin havainnot vapaasta liikkeestä:
              </p>
              <ul className="mt-4 space-y-2 text-muted-foreground">
                <li><strong className="text-foreground">Selälleen lattialle</strong> — vauva aloittaa selältä; hän kääntyy, ryömii ja nousee omassa tahdissaan.</li>
                <li><strong className="text-foreground">Ei istuteta pystyyn</strong> — asennot, joihin lapsi ei itse pääse, jarruttavat kehitystä.</li>
                <li><strong className="text-foreground">Suhdeperustainen hoiva</strong> — vaipanvaihto ja syöttäminen ovat lapsen kanssa, eivät lapselle.</li>
                <li><strong className="text-foreground">Pikler-kolmio</strong> — matala, vakaa teline, johon lapsi nousee itse silloin kun on valmis.</li>
              </ul>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Pikler kuoli 6. kesäkuuta 1984. Hänen tyttärensä Anna Tardos johti Pikler-instituuttia vuosikymmeniä ja jatkoi äitinsä tutkimustyötä.
              </p>
              <Link to="/montessori-koulu/emmi-pikler-elamakerta" className="mt-6 inline-flex text-sm font-medium text-foreground underline underline-offset-4 hover:text-clay">
                Lue laajempi elämäkerta →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONNECTION TO PRODUCTS */}
      <section className="border-b border-border bg-background">
        <div className="container-prose py-16 md:py-24">
          <div className="mb-10 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Filosofia arjessa</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
              Miten teoria muuttuu kalusteeksi
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Jokainen Montessori24:n tuote nojaa yhteen Montessorin tai Piklerin havaintoon. Emme myy leluja — myymme ympäristöjä, joissa lapsi saa näyttää mihin pystyy.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { to: "/pikler-kolmio", label: "Pikler-kolmio", desc: "Emmi Piklerin vapaan liikkeen periaate: vauva nousee itse ja laskeutuu itse. Ikäsuositus vaihtelee mallin mukaan." },
              { to: "/oppimistorni", label: "Oppimistorni", desc: "Maria Montessorin osallistumisen periaate: lapsi aikuisen työn tasolla, turvallisesti. 12 kk – 5 v." },
              { to: "/montessori-sanky", label: "Lattiasänky & -patja", desc: "Piklerin lattialta lähtevä uni. Lapsi säätää itse, milloin nousee, milloin lepää." },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="group rounded-2xl border border-border bg-cream p-7 transition-all hover:-translate-y-1 hover:border-foreground">
                <h3 className="font-display text-2xl text-foreground text-balance">{c.label}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
                <span className="mt-5 inline-flex text-sm font-medium text-foreground underline underline-offset-4 group-hover:text-clay">Katso mallit →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MONTESSORI IN FINLAND */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-16 md:py-24">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <span className="text-xs uppercase tracking-[0.25em] text-clay">Suomessa</span>
              <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
                Montessori Suomessa — lyhyt historia
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="text-lg leading-relaxed text-foreground">
                Maria Montessorin ajatukset saapuivat Suomeen jo 1910-luvulla lehtiartikkelien ja ulkomaisten opettajankoulutusten kautta. Ensimmäiset suomalaiset opettajat matkustivat 1920- ja 1930-luvuilla Roomaan ja Amsterdamiin osallistumaan Montessorin itsensä pitämille kansainvälisille kursseille.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Toisen maailmansodan jälkeen menetelmä juurtui laajemmin. <strong>Helsingin Montessori-yhdistys</strong> perustettiin 1950-luvulla ja se avasi ensimmäisiä Montessori-ryhmiä pääkaupunkiseudulle. 1970-luvulla perustettu <strong>Suomen Montessori-yhdistys ry</strong> alkoi kouluttaa opettajia järjestelmällisesti ja ylläpitää kansallista laatustandardia.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Nykyään Suomessa toimii kymmeniä Montessori-päiväkoteja ja -esiopetusryhmiä sekä useita alakouluja Helsingissä, Tampereella, Turussa, Oulussa ja Jyväskylässä. AMI-sertifioituja opettajia on yhä enemmän, ja Helsingissä syntynyt <Link to="/montessori-koulu/hei-koulujarjestelma-historia" className="underline underline-offset-4 hover:text-clay">HEI Schools</Link> -konsepti vie suomalaista varhaiskasvatusta maailmalle.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                2010-luvulla ajattelu siirtyi kouluista koteihin. Suomalaiset vanhemmat löysivät oppimistornin, Pikler-kolmion ja lattiapatjan — työkalut, joilla arjen jokainen hetki (tiskaus, pukeminen, nukkumaan meno) voi olla myös oppimishetki. Montessori24 syntyi tästä liikkeestä: koivuvaneria, kestäviä liitoksia ja skandinaavista pelkistyneisyyttä perheille, jotka haluavat rakentaa kotinsa lapsen ehdoilla.
              </p>
              <Link to="/montessori-koulu/montessori-historia-suomi" className="mt-6 inline-flex text-sm font-medium text-foreground underline underline-offset-4 hover:text-clay">
                Syvennä: Montessori-pedagogiikan historia Suomessa →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="border-b border-border bg-background">
        <div className="container-prose py-16 md:py-24">
          <div className="mb-12 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Aikajana</span>
            <h2 className="mt-3 font-display text-3xl text-foreground md:text-5xl text-balance">
              150 vuotta lapsen ehdoilla
            </h2>
          </div>
          <ol className="relative border-l-2 border-border pl-8 md:pl-10">
            {TIMELINE.map((m) => (
              <li key={m.year} className="relative mb-10 last:mb-0">
                <span className="absolute -left-[41px] md:-left-[49px] top-1 grid h-6 w-6 place-items-center rounded-full border-2 border-clay bg-background">
                  <span className="h-2 w-2 rounded-full bg-clay" />
                </span>
                <p className="font-display text-2xl text-clay">{m.year}</p>
                <h3 className="mt-1 font-display text-xl text-foreground text-balance md:text-2xl">{m.title}</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-[15px]">{m.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream">
        <div className="container-prose py-20 md:py-28 text-center">
          <h2 className="font-display text-3xl text-foreground md:text-5xl text-balance mx-auto max-w-2xl">
            Tuo tämä perintö omaan kotiisi
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
            Kalusteet, joissa 100 vuoden tutkimus muuttuu koivuvaneriksi ja pehmeiksi reunoiksi — arkeen, jonka lapsesi muistaa.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/kauppa" className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm uppercase tracking-[0.2em] text-background hover:bg-foreground/85">
              Tutustu mallistoomme
            </Link>
            <Link to="/koulutus" className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm uppercase tracking-[0.2em] text-foreground hover:border-foreground">
              Koulutus & opas →
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
